import {useMemo} from 'react';

import {
  LinePlotTemplateSettings,
  WorkspaceTemplate,
} from '../../../graphql/workspaceTemplates';
import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {useRampFlagUseBackendSmoothing} from '../../../util/rampFeatureFlags';
import {LinePlotSettings} from '../../WorkspaceDrawer/Settings/types';
import {LINE_PLOT_TEMPLATE_FIELDS} from '../constants';
import {LINE_PLOT_TEMPLATE_DISPLAY_FNS} from './linePlotTemplateDisplayFns';

/**
 * This hook is used in both the template drawer and the user settings page.
 * In both pages, we want to display the same data; however, the template
 * drawer needs to handle strikethrough old values when relevant.
 */
export const useTemplateSections = ({
  workspaceTemplate,
  currWorkspaceLinePlotSettings,
}: {
  workspaceTemplate: WorkspaceTemplate;
  currWorkspaceLinePlotSettings?: LinePlotSettings;
}) => {
  const {entityName} = useProjectParams();
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing(entityName);

  const linePlotTemplateSections = useLinePlotTemplateSections({
    currTemplateLinePlotSettings: workspaceTemplate?.linePlotSettings,
    currWorkspaceLinePlotSettings,
    isBackendSmoothingEnabled,
  });

  return [...linePlotTemplateSections];
};

const useLinePlotTemplateSections = ({
  currTemplateLinePlotSettings,
  currWorkspaceLinePlotSettings,
  isBackendSmoothingEnabled,
}: {
  currTemplateLinePlotSettings: WorkspaceTemplate['linePlotSettings'];
  currWorkspaceLinePlotSettings?: LinePlotSettings;
  isBackendSmoothingEnabled: boolean;
}) => {
  return useMemo(() => {
    return [
      {
        title: 'Data',
        rows: LINE_PLOT_TEMPLATE_FIELDS.map(key => {
          return LINE_PLOT_TEMPLATE_DISPLAY_FNS[key]({
            isBackendSmoothingEnabled,
            currTemplateLinePlotSettings,
            currWorkspaceLinePlotSettings,
          });
        }),
      },
    ];
  }, [
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
    isBackendSmoothingEnabled,
  ]);
};

/**
 * These are values that should be copied over to the default spec when
 * a template is applied.
 */
export const getLinePlotTemplateValues = (
  workspaceTemplate: WorkspaceTemplate | undefined
): LinePlotSettings => {
  const templateLinePlotSettings = workspaceTemplate?.linePlotSettings;
  if (templateLinePlotSettings == null) {
    return {};
  }

  const linePlotSettings = LINE_PLOT_TEMPLATE_FIELDS.reduce<LinePlotSettings>(
    (acc, key: keyof LinePlotTemplateSettings) => {
      const templateValue = templateLinePlotSettings[key];

      // If a template value is undefined, there's no need to copy it over to spec
      // since that's the default already
      if (templateValue != null) {
        // This is safe to do since we've constrainted the types from the query already
        acc[key] = templateValue as any;
      }
      return acc;
    },
    {}
  );

  return linePlotSettings;
};
